/**
 * Client-side PDF text extraction using the bundled pdfjs-dist.
 *
 * pdfjs-dist ships with the app (no CDN). Its worker is NOT bundler-emitted via
 * `new URL(..., import.meta.url)` — that path is broken here (pdfjs-dist is in
 * next.config `serverExternalPackages`, so the build refuses to emit the asset;
 * see PDF_WORKER_SRC below). Instead the worker is served as a static,
 * same-origin file from public/, copied version-matched by
 * scripts/copy-pdf-worker.mjs at postinstall. This replaced the previous
 * approach of loading pdf-parse from the jsDelivr CDN through an injected inline
 * module script. That approach hung forever: the unpinned `pdf-parse@latest` web
 * build code-splits at runtime via `import.meta.url`, which for an inline
 * module resolves to the PAGE origin (not the CDN), so the split chunk 404'd —
 * and the old loader had no error/timeout path, so its load promise never
 * settled (infinite spinner, no error). Surfaced on the client-side deck text
 * extraction path (onboarding clientTextPreview).
 *
 * Client-only: imported by landing/onboarding client components (see
 * IdeaChatInput, PitchUploader, NewSourceDialog).
 */

// fixed on 2026-09-17: Sentry 7676752953
import { PDF_NO_TEXT_ERROR } from "@/constants/deckParse";
export { PDF_NO_TEXT_ERROR } from "@/constants/deckParse";

/**
 * Static, same-origin worker path. The file is copied into public/ from the
 * installed pdfjs-dist by scripts/copy-pdf-worker.mjs (postinstall), so it is
 * always version-matched to the bundled lib. We MUST NOT resolve the worker via
 * `new URL("pdfjs-dist/build/pdf.worker.min.mjs", import.meta.url)`: because
 * pdfjs-dist is in next.config `serverExternalPackages` (kept external for the
 * server-side vision pipeline), the Next build rejects that worker asset
 * request ("matches serverExternalPackages ... require() resolves to an
 * EcmaScript module") and never emits the file, so the worker never loads and
 * getDocument hangs forever. The client `import("pdfjs-dist")` itself is
 * unaffected — only the `new URL` asset emission is.
 */
const PDF_WORKER_SRC = "/pdf.worker.min.mjs";

/** Hard ceiling so a misconfigured worker surfaces an error instead of an infinite spinner. */
const PARSE_TIMEOUT_MS = 30_000;

/**
 * Lazily import pdfjs-dist and point it at the bundled, same-origin worker.
 * `import("pdfjs-dist")` is module-cached; `workerSrc` is a constant, so
 * assigning it every call is an idempotent no-op — no guard needed.
 */
async function loadPdfjs() {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
  return pdfjs;
}

/**
 * Reject if `promise` doesn't settle within `ms` — never hang the UI silently.
 * Named to avoid confusion with the AbortSignal-based `withTimeout` in the
 * server-only `src/lib/utils/server.ts`.
 */
function raceTimeout<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(message)), ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer));
}

/**
 * Sanitize text to remove null bytes and ensure valid UTF-8 encoding.
 * PostgreSQL doesn't allow null bytes in text fields.
 */
export function sanitizePdfText(text: string): string {
  let sanitized = text.replace(/\0/g, "");

  // Decode-then-encode replaces invalid UTF-8 sequences with the U+FFFD char.
  try {
    const encoder = new TextEncoder();
    const decoder = new TextDecoder("utf-8", { fatal: false });
    const bytes = encoder.encode(sanitized);
    sanitized = decoder.decode(bytes);
  } catch {
    /* benign: encoding failures fall back to aggressive character stripping */
    sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F]/g, "");
  }

  return sanitized;
}

/**
 * Parse a PDF file to sanitized text using bundled, client-side pdfjs-dist.
 * Throws if the file is not a PDF, parsing fails, or no text is extracted.
 */
export async function parsePdfToText(file: File): Promise<string> {
  if (!file.type.includes("pdf") && !file.name.toLowerCase().endsWith(".pdf")) {
    throw new Error("File must be a PDF");
  }

  // The 30s ceiling MUST cover the ENTIRE pipeline — the lazy pdfjs import and
  // file.arrayBuffer() included, not just page extraction. A stalled import
  // chunk or a slow file read is exactly the kind of hang this guarantee exists
  // to prevent (PR #534 review): if the deadline started only after those
  // awaits, either could spin indefinitely outside it. loadingTask is captured
  // from inside so the finally can tear it (and its worker port) down even when
  // the timeout wins — pdfjs has no abort hook — and stays undefined if we never
  // got far enough to create it.
  let loadingTask:
    | ReturnType<Awaited<ReturnType<typeof loadPdfjs>>["getDocument"]>
    | undefined;
  // Set once the race is over (timeout won, or success/error already returned).
  // If getDocument only runs AFTER this — the import/file-read was still pending
  // when the timeout fired — the outer finally already ran with loadingTask
  // undefined and won't fire again, so the late task must tear ITSELF down or it
  // leaks a worker after the UI reported failure (#534 review).
  let raceSettled = false;

  const parse = async (): Promise<string> => {
    const pdfjs = await loadPdfjs();
    const buffer = await file.arrayBuffer();
    const task = pdfjs.getDocument({ data: new Uint8Array(buffer) });
    loadingTask = task;
    if (raceSettled) {
      // The timeout already won while we were importing/reading — the finally
      // won't destroy this late task, so destroy it here and abandon.
      void task.destroy().catch(() => {});
      throw new Error("PDF parsing abandoned after timeout");
    }
    const pdf = await task.promise;
    const pageTexts: string[] = [];
    for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const pageText = content.items
        .map((item) => ("str" in item ? item.str : ""))
        .join(" ");
      pageTexts.push(pageText);
      // Free per-page resources as we go (large decks stay bounded).
      page.cleanup();
    }
    // Sanitize FIRST, then trim: stripping null bytes can leave surrounding
    // whitespace (e.g. "\0 \0" → " "), and trimming afterward keeps the
    // downstream `if (!text)` no-text guard effective (#534 review).
    return sanitizePdfText(pageTexts.join("\n")).trim();
  };

  try {
    // Attach a no-op catch so a late rejection (after raceTimeout already won)
    // isn't unhandled; the real error still propagates through raceTimeout.
    const parsePromise = parse();
    void parsePromise.catch(() => {});

    try {
      const text = await raceTimeout(
        parsePromise,
        PARSE_TIMEOUT_MS,
        "PDF parsing timed out — the file may be too large or corrupted.",
      );
      if (!text) {
        throw new Error(PDF_NO_TEXT_ERROR);
      }
      return text;
    } finally {
      // Mark the race over so a late getDocument (import/file-read that resolves
      // after the timeout) tears itself down instead of leaking.
      raceSettled = true;
      // Destroy the loading task (document + worker port) on success, error,
      // AND timeout — stops the background parse when the timeout wins. Skipped
      // if the stall happened before getDocument ran (loadingTask undefined);
      // that late task self-destructs via the raceSettled check above.
      // Fire-and-forget: a wedged worker may never acknowledge terminate, so
      // AWAITING destroy() here would re-introduce the exact infinite hang the
      // timeout exists to prevent (#534 review). The .catch keeps failures
      // contained.
      void loadingTask?.destroy().catch(() => {});
    }
  } catch (error) {
    // Callers capture with captureClientError + impact tag; raw Sentry.captureException
    // here would win the Dedupe dedup and strip the caller's userImpact tag (Sentry 7676752953).
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(`Failed to parse PDF: ${String(error)}`);
  }
}
